/**
 * Le panneau de droite du test — Francis · Vault · Scripts · Aide.
 *
 * 🎬 « Vault » arrive le 06-10-2026. Kayla : « c'est important de connaître le
 * contenu de la bouteille pour pouvoir vendre ». Chaque média y a sa FICHE —
 * durée, ce qu'on y voit, prix — exactement ce qu'il doit savoir dire au fan.
 * La même fiche s'affiche sous le média choisi dans « Délivrer au paiement ».
 *
 * Copie la forme du poste (Kayla, 17-09-2026 : « tout mettre dans le même
 * panneau : Francis, Infos, Scripts, et qui voit Francis en main ») : des
 * onglets, Francis ouvert d'office. « Infos » (la fiche du fan) n'a rien à
 * dire d'un faux fan ; « Aide » prend sa place — c'est l'introduction, à
 * relire.
 *
 * ⛔ Rien ne part d'ici, comme au poste : « Insérer » pose le texte dans le
 * composeur, « Préparer la vente » ouvre la Transaction remplie. C'est le
 * bouton d'envoi du candidat qui part.
 */
import { useState } from "react";

import { Button, Carte, Etiquette, Ligne, Lignes, Onglets, Section, Vide } from "@/shared/ui";
import { ArrowLeft, Sparkles } from "@/shared/ui/icons";

import { AideDuPoste } from "./AideDuPoste";
import { conseilDeFrancis } from "./conseils";
import type { Etat } from "./moteur";
import type { PreRemplissage } from "./TransactionModale";
import { SCRIPTS, VAULT, type DossierScript, type MediaDuVault } from "./scenario";

type OngletDuPanneau = "francis" | "vault" | "scripts" | "aide";

const ONGLETS: ReadonlyArray<{ valeur: OngletDuPanneau; etiquette: string }> = [
  { valeur: "francis", etiquette: "Francis" },
  { valeur: "vault", etiquette: "Vault" },
  { valeur: "scripts", etiquette: "Scripts" },
  { valeur: "aide", etiquette: "Aide" },
];

interface Props {
  etat: Etat;
  onInserer: (texte: string, origine: "script" | "francis") => void;
  onPreparerVente: (vente: PreRemplissage) => void;
}

export function PanneauDeDroite({ etat, onInserer, onPreparerVente }: Props) {
  const [onglet, setOnglet] = useState<OngletDuPanneau>("francis");

  return (
    <Carte
      remplissage="aucun"
      role="complementary"
      aria-label="Le panneau de droite"
      className="flex min-h-[320px] w-full shrink-0 flex-col min-[900px]:min-h-0 min-[900px]:w-[clamp(300px,30%,400px)]"
    >
      <div className="shrink-0 p-3">
        <Onglets
          style="segments"
          taille="md"
          pleineLargeur
          nom="Les onglets du panneau"
          valeur={onglet}
          onChange={setOnglet}
          options={ONGLETS}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5 pt-1">
        {onglet === "francis" && (
          <OngletFrancis etat={etat} onInserer={onInserer} onPreparerVente={onPreparerVente} />
        )}
        {onglet === "vault" && <OngletVault />}
        {onglet === "scripts" && (
          <OngletScripts onInserer={(t) => onInserer(t, "script")} onPreparerVente={onPreparerVente} />
        )}
        {onglet === "aide" && <AideDuPoste />}
      </div>
    </Carte>
  );
}

function OngletFrancis({ etat, onInserer, onPreparerVente }: Props) {
  const conseil = conseilDeFrancis(etat, etat.ouvert);
  // Le résumé arrive AU CLIC, comme au poste (Kayla, 23-09 : « plus rapides,
  // moins chères ») — et il retombe quand on change de fan.
  const [resumeDe, setResumeDe] = useState<string | null>(null);

  if (!conseil) {
    return (
      <Vide
        taille="sm"
        titre="Ouvre une conversation de « Ma file » : je te dirai où en est le fan et quoi faire."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 text-[15px] leading-relaxed">
      <Etiquette icone={<Sparkles aria-hidden />}>Conseil</Etiquette>

      {resumeDe === etat.ouvert ? (
        <p className="text-fg">{conseil.resume}</p>
      ) : (
        <Button variant="secondary" size="lg" className="min-h-10 self-start" onClick={() => setResumeDe(etat.ouvert)}>
          Résumer ce fan
        </Button>
      )}

      <p className="text-fg">
        <span className="text-muted">Où il en est · </span>
        {conseil.ouIlEnEst}
      </p>
      <p className="text-fg">
        <span className="text-muted">Quoi faire · </span>
        {conseil.quoiFaire}
      </p>

      {conseil.message && (
        <Section titre="Message">
          <p className="text-[14px] leading-snug text-fg">{conseil.message}</p>
          <Button
            variant="secondary"
            size="lg"
            className="mt-3 min-h-10"
            onClick={() => onInserer(conseil.message!, "francis")}
          >
            Insérer
          </Button>
        </Section>
      )}

      {conseil.vente && (
        <Section titre="Vente">
          <Button
            variant="accent"
            size="lg"
            className="min-h-10"
            onClick={() => onPreparerVente(conseil.vente!)}
          >
            Préparer la vente · {conseil.vente.prixCents / 100} €
          </Button>
        </Section>
      )}
    </div>
  );
}

/**
 * Les scripts de Kayla, rangés par dossier — l'onglet Scripts du poste.
 * « Insérer » met la phrase dans le champ ; il relit, il envoie. Un message
 * BOUTON DE TRANSACTION (`prixCents`) ouvre « Nouvelle transaction » à son
 * prix, comme au poste — produit et média restent à choisir.
 */
function OngletScripts({
  onInserer,
  onPreparerVente,
}: {
  onInserer: (texte: string) => void;
  onPreparerVente: (vente: PreRemplissage) => void;
}) {
  const [dossier, setDossier] = useState<DossierScript | null>(null);

  if (!dossier) {
    return (
      <Lignes ariaLabel="Les dossiers de scripts">
        {SCRIPTS.map((d) => (
          <Ligne
            key={d.nom}
            etiquette={d.nom}
            valeur={`${d.messages.length} message${d.messages.length > 1 ? "s" : ""}`}
            onClick={() => setDossier(d)}
          />
        ))}
      </Lignes>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Button
        variant="ghost"
        size="lg"
        className="-ml-3 min-h-10 self-start"
        icon={<ArrowLeft aria-hidden className="h-4 w-4" />}
        onClick={() => setDossier(null)}
      >
        Tous les scripts
      </Button>
      <p className="px-1 text-[15px] font-semibold text-fg">{dossier.nom}</p>
      <ul className="flex flex-col gap-2">
        {dossier.messages.map((m) => (
          <li key={m.titre}>
            <Carte remplissage="sm">
              <Etiquette>{m.titre}</Etiquette>
              <p className="mt-1.5 text-[14px] leading-snug text-fg">{m.texte}</p>
              {m.prixCents ? (
                <Button
                  variant="accent"
                  size="lg"
                  className="mt-3 min-h-10"
                  onClick={() => onPreparerVente({ offreCle: null, prixCents: m.prixCents!, mediaId: null })}
                >
                  Transaction · {m.prixCents / 100} €
                </Button>
              ) : (
                <Button variant="secondary" size="lg" className="mt-3 min-h-10" onClick={() => onInserer(m.texte)}>
                  Insérer
                </Button>
              )}
            </Carte>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Les médias du Vault, chacun avec sa fiche — l'onglet Vault. */
function OngletVault() {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] leading-relaxed text-muted">
        Ce que tu vends, et ce qu'il y a dedans. Décris-le au fan avec tes mots.
      </p>
      {VAULT.map((m) => (
        <FicheDuMedia key={m.id} fiche={m} />
      ))}
    </div>
  );
}

/** La fiche d'un média — servie aussi dans « Délivrer au paiement ». */
export function FicheDuMedia({ fiche, compacte = false }: { fiche: MediaDuVault; compacte?: boolean }) {
  return (
    <Carte remplissage={compacte ? "sm" : "md"} role="group" aria-label={`Fiche : ${fiche.titre}`}>
      {!compacte && (
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[16px] font-semibold text-fg">{fiche.titre}</span>
          <span className="shrink-0 text-[15px] font-semibold tabular-nums text-fg">{fiche.prixCents / 100} €</span>
        </div>
      )}
      <div className={compacte ? undefined : "mt-1"}>
        <Etiquette>{fiche.duree}</Etiquette>
      </div>
      <ul className="mt-2 flex flex-col gap-1">
        {fiche.contenu.map((l) => (
          <li key={l} className={compacte ? "text-[13px] leading-snug text-fg" : "text-[14px] leading-snug text-fg"}>
            · {l}
          </li>
        ))}
      </ul>
    </Carte>
  );
}
